/**
 * TopologyViewerPage - Graph visualizer for agent workflows
 *
 * T7.3 Enhancement: Mobile-responsive canvas with touch gestures.
 * Uses useContainerSize for dynamic SVG sizing and useTouchGestures for pan/zoom.
 *
 * Integrates:
 * - Real agent data from topology API
 * - Simple force-directed visualization
 * - Topology statistics
 * - View switching (deploy-feature vs all-agents)
 * - Responsive layout with mobile sidebar
 *
 * Copyright (c) 2025-2026 Pareidolia LLC. All rights reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getDeployFeatureTopology, getAllAgentsTopology, Topology as APITopology, AgentNode } from '../../api/topology';
import { useContainerSize } from '../../hooks/useContainerSize';
import { useTouchGestures } from '../../hooks/useTouchGestures';

interface TopologyNode {
  id: string;
  label: string;
  type: string;
  name?: string;
  capabilities?: string[];
  category?: string;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
}

interface TopologyEdge {
  from: string;
  to: string;
  type: string;
  label: string;
  weight?: number;
}

interface Topology {
  namespace: string;
  version: string;
  type: string;
  nodes: TopologyNode[];
  edges: TopologyEdge[];
}

function convertAPITopology(apiTopology: APITopology): Topology {
  return {
    namespace: apiTopology.namespace,
    version: apiTopology.version,
    type: apiTopology.type,
    nodes: apiTopology.nodes.map((node: AgentNode) => ({
      id: node.id,
      label: node.name,
      type: 'agent',
      name: node.name,
      capabilities: node.capabilities,
      category: node.category
    })),
    edges: apiTopology.edges
  };
}

export const TopologyViewerPage: React.FC = () => {
  const [currentView, setCurrentView] = useState<'deploy-feature' | 'all-agents'>('deploy-feature');
  const [topology, setTopology] = useState<Topology>(convertAPITopology(getDeployFeatureTopology()));
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [showSidebar, setShowSidebar] = useState(false);

  const { ref: containerRef, width: containerWidth, height: containerHeight } = useContainerSize({
    defaultWidth: 800,
    defaultHeight: 600,
  });

  const { viewBox, handlers: touchHandlers, resetView } = useTouchGestures({
    initialWidth: containerWidth,
    initialHeight: containerHeight,
  });

  // Center coordinates for force simulation
  const centerX = containerWidth / 2;
  const centerY = containerHeight / 2;

  // Force-directed layout simulation
  const runSimulation = useCallback((topo: Topology, cx: number, cy: number) => {
    const nodes = [...topo.nodes];
    const edges = [...topo.edges];

    nodes.forEach((node, i) => {
      if (!node.x || !node.y) {
        node.x = cx + Math.cos((i / nodes.length) * 2 * Math.PI) * Math.min(cx, cy) * 0.6;
        node.y = cy + Math.sin((i / nodes.length) * 2 * Math.PI) * Math.min(cx, cy) * 0.6;
        node.vx = 0;
        node.vy = 0;
      }
    });

    const iterations = 50;
    const springStrength = 0.01;
    const repulsionStrength = 500;
    const centeringStrength = 0.001;
    const damping = 0.9;

    for (let iter = 0; iter < iterations; iter++) {
      edges.forEach(edge => {
        const source = nodes.find(n => n.id === edge.from);
        const target = nodes.find(n => n.id === edge.to);
        if (source && target && source.x !== undefined && target.x !== undefined) {
          const dx = target.x - source.x;
          const dy = target.y! - source.y!;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const force = (dist - 150) * springStrength;
          const fx = (dx / dist) * force;
          const fy = (dy / dist) * force;
          source.vx! += fx;
          source.vy! += fy;
          target.vx! -= fx;
          target.vy! -= fy;
        }
      });

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const nodeA = nodes[i];
          const nodeB = nodes[j];
          if (nodeA.x !== undefined && nodeB.x !== undefined) {
            const dx = nodeB.x - nodeA.x;
            const dy = nodeB.y! - nodeA.y!;
            const dist = Math.sqrt(dx * dx + dy * dy) || 1;
            const force = repulsionStrength / (dist * dist);
            const fx = (dx / dist) * force;
            const fy = (dy / dist) * force;
            nodeA.vx! -= fx;
            nodeA.vy! -= fy;
            nodeB.vx! += fx;
            nodeB.vy! += fy;
          }
        }
      }

      nodes.forEach(node => {
        if (node.x !== undefined) {
          node.vx! += (cx - node.x) * centeringStrength;
          node.vy! += (cy - node.y!) * centeringStrength;
        }
      });

      nodes.forEach(node => {
        if (node.x !== undefined) {
          node.vx! *= damping;
          node.vy! *= damping;
          node.x += node.vx!;
          node.y! += node.vy!;
        }
      });
    }

    return { ...topo, nodes };
  }, []);

  useEffect(() => {
    setTopology(prev => runSimulation(prev, centerX, centerY));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleViewSwitch = (view: 'deploy-feature' | 'all-agents') => {
    setCurrentView(view);
    const rawTopo = view === 'deploy-feature'
      ? convertAPITopology(getDeployFeatureTopology())
      : convertAPITopology(getAllAgentsTopology());
    setTopology(runSimulation(rawTopo, centerX, centerY));
    setSelectedNode(null);
    resetView();
  };

  const handleNodeSelect = (nodeId: string) => {
    setSelectedNode(nodeId);
    setShowSidebar(true);
  };

  const stats = {
    totalNodes: topology.nodes.length,
    totalEdges: topology.edges.length,
    bottlenecks: topology.nodes.filter(n => {
      const inDegree = topology.edges.filter(e => e.to === n.id).length;
      const outDegree = topology.edges.filter(e => e.from === n.id).length;
      return inDegree > 2 || outDegree > 2;
    }).length
  };

  const selectedNodeData = topology.nodes.find(n => n.id === selectedNode);

  return (
    <div className="flex flex-col md:flex-row h-screen bg-gray-100">
      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="p-3 md:p-4 bg-white border-b border-gray-300">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div>
              <h2 className="m-0 text-lg md:text-xl">Agent Topology Viewer</h2>
              <p className="mt-1 mb-0 text-gray-500 text-xs md:text-sm">
                {topology.namespace} v{topology.version}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleViewSwitch('deploy-feature')}
                className={`px-3 py-1.5 md:px-4 md:py-2 border-none rounded cursor-pointer text-xs md:text-sm ${
                  currentView === 'deploy-feature'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Deploy Feature
              </button>
              <button
                onClick={() => handleViewSwitch('all-agents')}
                className={`px-3 py-1.5 md:px-4 md:py-2 border-none rounded cursor-pointer text-xs md:text-sm ${
                  currentView === 'all-agents'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                All Agents
              </button>
            </div>
          </div>
        </div>

        {/* Responsive Canvas Container */}
        <div ref={containerRef} className="flex-1 overflow-hidden p-2 md:p-5">
          <svg
            width={containerWidth}
            height={containerHeight}
            viewBox={viewBox}
            className="bg-white border-2 border-gray-300 rounded-lg w-full h-full touch-none"
            {...touchHandlers}
          >
            <defs>
              <marker
                id="arrowhead"
                viewBox="0 0 10 10"
                refX="25"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#999" />
              </marker>
            </defs>

            {/* Edges */}
            <g>
              {topology.edges.map((edge, i) => {
                const source = topology.nodes.find(n => n.id === edge.from);
                const target = topology.nodes.find(n => n.id === edge.to);
                if (!source || !target || !source.x || !target.x) return null;

                const color = edge.type === 'triggers' ? '#3b82f6' : '#999';

                return (
                  <g key={i}>
                    <line
                      x1={source.x}
                      y1={source.y}
                      x2={target.x}
                      y2={target.y}
                      stroke={color}
                      strokeWidth="2"
                      strokeOpacity="0.6"
                      markerEnd="url(#arrowhead)"
                    />
                    <text
                      x={(source.x + target.x) / 2}
                      y={(source.y! + target.y!) / 2}
                      fontSize="10"
                      fill="#666"
                      textAnchor="middle"
                    >
                      {edge.label}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Nodes */}
            <g>
              {topology.nodes.map((node) => {
                if (!node.x || !node.y) return null;
                const isSelected = selectedNode === node.id;

                return (
                  <g
                    key={node.id}
                    onClick={() => handleNodeSelect(node.id)}
                    className="cursor-pointer"
                  >
                    <circle
                      cx={node.x}
                      cy={node.y}
                      r={isSelected ? 25 : 20}
                      fill={isSelected ? '#8b5cf6' : '#3b82f6'}
                      stroke="white"
                      strokeWidth="2"
                    />
                    <text
                      x={node.x + 28}
                      y={node.y + 5}
                      fontSize="12"
                      fill="#333"
                    >
                      {node.label}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
        </div>

        {/* Footer Stats */}
        <div className="py-2 md:py-3 px-3 md:px-4 bg-white border-t border-gray-300 text-xs md:text-sm text-gray-500">
          {stats.totalNodes} nodes · {stats.totalEdges} edges · {stats.bottlenecks} bottlenecks
        </div>
      </div>

      {/* Right Sidebar - Desktop: side panel, Mobile: bottom sheet */}
      {selectedNode && showSidebar && (
        <>
          {/* Mobile backdrop */}
          <div
            className="fixed inset-0 bg-black/30 z-40 md:hidden"
            onClick={() => setShowSidebar(false)}
          />

          {/* Sidebar content */}
          <div className="fixed bottom-0 inset-x-0 z-50 md:relative md:z-auto md:bottom-auto md:inset-x-auto
                          w-full md:w-[300px] max-h-[60vh] md:max-h-none
                          bg-white border-t md:border-t-0 md:border-l border-gray-300
                          rounded-t-xl md:rounded-none
                          p-4 md:p-5 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="m-0 text-base md:text-lg">Node Details</h3>
              <button
                onClick={() => { setSelectedNode(null); setShowSidebar(false); }}
                className="px-2 py-1 bg-gray-200 border-none rounded cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Drag handle for mobile */}
            <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-3 md:hidden" />

            <div className="mb-4">
              <strong className="text-sm">ID:</strong><br />
              <span className="text-sm text-gray-700 break-all">{selectedNode}</span>
            </div>

            <div className="mb-4">
              <strong className="text-sm">Type:</strong><br />
              <span className="text-sm text-gray-700">{selectedNodeData?.type}</span>
            </div>

            {selectedNodeData?.category && (
              <div className="mb-4">
                <strong className="text-sm">Category:</strong><br />
                <span className="text-sm text-gray-700">{selectedNodeData.category}</span>
              </div>
            )}

            {selectedNodeData?.capabilities && selectedNodeData.capabilities.length > 0 && (
              <div className="mb-4">
                <strong className="text-sm">Capabilities:</strong>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedNodeData.capabilities.map((cap, i) => (
                    <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">
                      {cap}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <strong className="text-sm">Dependencies:</strong><br />
              <span className="text-sm text-gray-700">
                {topology.edges
                  .filter(e => e.from === selectedNode)
                  .map(e => topology.nodes.find(n => n.id === e.to)?.label)
                  .join(', ') || 'None'}
              </span>
            </div>

            <div className="mb-4">
              <strong className="text-sm">Depended On By:</strong><br />
              <span className="text-sm text-gray-700">
                {topology.edges
                  .filter(e => e.to === selectedNode)
                  .map(e => topology.nodes.find(n => n.id === e.from)?.label)
                  .join(', ') || 'None'}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
